import { Inject, Injectable, Optional, SkipSelf } from "@angular/core";

import { HttpClient, HttpParams  } from "@angular/common/http";
import { Observable } from 'rxjs';

import { AccesTLR } from '../entities/acces-tlr.entity';
import { AccesSearchResult } from '../entities/acces-search-result';
import { AccesSearchCriteria } from '../entities/acces-search-criteria';
import { AccesDtoForWidget } from '../entities/acces-dto-for-widget.entity';
import { AccesOperationTLR } from '../entities/acces-operation-tlr.entity';
import { AccesOperation } from '../entities/acces-operation.entity';
import { AccesIncomplet } from '../entities/acces-incomplet.entity';
import { AssociationArretsOrder } from '../entities/association-arrets-order.entity';
import { FermetureAccesOrder } from '../entities/fermeture-acces-order.entity';
import { DemandeIntervention } from "../entities/demande-intervention.entity";
import { OuvrageAccesDTO } from '../entities/ouvrage-acces-dto.entity';
import { WorkOrder } from '../entities/work-order.entity';
import { Notification } from '../entities/notification.entity';

import { Suggestions } from '../../../../shared/suggestable-input/entities/suggestions.entity';

import { JSR310ConversionService } from '../../../../shared/jsr310-localdates/services/jsr310-conversion.service';
import { AccesOperationBinderEntity } from '../entities/acces-operation-binder.entity';

import { AccesEtat } from '../../../../app.constants';
import { CreationDeniedAccesOrder } from "../entities/creation-denied-acces-order.entity";
import { CreationGrantedAccesOrder } from "../entities/creation-granted-acces-order.entity";
import { CreationPlannedAccesOrder } from "../entities/creation-planned-acces-order.entity";
import { CreationTarifNegatifAccesOrder } from "../entities/creation-tarif-negatif-acces-order.entity";
import { EntrepriseAccesDTO } from "../entities/entreprise-acces-dto.entity";
import { ErrorAccesOrder } from "../entities/error-acces-order.entity";
import { IntervenantAccesDTO } from "../entities/intervenant-acces-dto.entity";
import { MassChangeEtatOrder } from "../entities/mass-change-etat-acces-order.entity";
import { MassCloseAccesOrder } from "../entities/mass-close-acces-order.entity";
import { MassDenyAccesOrder } from "../entities/mass-deny-acces-order.entity";
import { UpdateGrantedAccesOrder } from "../entities/update-granted-acces-order.entity";
import { UpdatePlannedAccesOrder } from "../entities/update-planned-acces-order.entity";
import { UpdateAndGrantAccesOrder } from "../entities/update-and-grant-acces-order.entity";
import { UpdateAndDenyAccesOrder } from "../entities/update-and-deny-acces-order.entity";
import { UpdateRappelIntervenantsOrder } from '../entities/update-rappel-intervenants-order.entity';
import { UpdateTarifNegatifAccesOrder } from "../entities/update-tarif-negatif-acces-order.entity";
import { AccesPilotageEcoOrder } from "../entities/acces-pilotage-eco-order";
import { DemandeFermetureValidationOrder } from "../entities/demande-fermeture-validation-order";
import { NotificationForCC } from "../entities/notification-for-cc.entity";

@Injectable({ providedIn: 'root' })
export class AccesService {
	private path: string;

	constructor( //
		@Inject(HttpClient) private httpClient: HttpClient, //
		@Inject(JSR310ConversionService) private jsr310Service: JSR310ConversionService, 
		@Optional() @SkipSelf() previousInstance?: AccesService //
	) {
		if (previousInstance) {
			throw new Error(
				'This service is already loaded. Import it in the AppModule only');
		}
		this.path = (window as any).baseApiPath;
	}
	
	public getById(accesId: number): Observable<AccesTLR> {
		let url = this.path + "acces/" + accesId + "?format=TLR";
		let result = this.httpClient //
			.get<AccesTLR>(url) //
		;
		return result;
	}

	public getByOperationId(operationId: number): Observable<AccesTLR[]> {
		let url = this.path + "interventions/" + operationId + "/acces";
		let result = this.httpClient //
			.get<AccesTLR[]>(url) //
		;

		return result;
	}

	// Methode permettant de recuperer les acces en cours sur un parc
	public getEnCoursByParc(parcId: number): Observable<AccesTLR[]> {
		let url = this.path + 'parcs/' + parcId + '/acces-en-cours';
		return this.httpClient.get<AccesTLR[]>(url);
	}
	
	// Methode permettant de recuperer les acces en cours sur l'ouvrage, son parent ou ses fils
	public getEnCoursByOuvrage(ouvrageId: number, depth?: number): Observable<AccesTLR[]> {
		let url = this.path + 'ouvrages/' + ouvrageId + '/acces-coactifs';
		if (depth !== undefined && depth != null) {
			url += "?depth=" + depth;
		}
		return this.httpClient.get<AccesTLR[]>(url);
	}

	// Methode permettant de recuperer les acces en cours sur une antenne
	public getEnCoursByAntenne(idAntenne: number): Observable<AccesTLR[]> {
		let url = this.path + 'antennes/' + idAntenne + '/acces-en-cours';
		return this.httpClient.get<AccesTLR[]>(url);
	};
	
	// Chargement de tous les acces EN COURS d'une antenne
	public getEnCours(): Observable<AccesTLR[]> {
		let url = this.path + 'acces/etat/en_cours?format=TLR';
		return this.httpClient.get<AccesTLR[]>(url)
	};

	// Methode permettant de recuperer les acces en cours sur une antenne
	public getEnCoursByAntenneForWidget(idAntenne: number): Observable<AccesDtoForWidget[]> {
		let url = this.path + 'antennes/' + idAntenne + '/acces-en-cours?format=DTOForWidget';
		return this.httpClient.get<AccesDtoForWidget[]>(url);
	};
	
	// Chargement de tous les acces EN COURS d'une antenne
	public getEnCoursForWidget(): Observable<AccesDtoForWidget[]> {
		let url = this.path + 'acces/etat/en_cours?format=DTOForWidget';
		return this.httpClient.get<AccesDtoForWidget[]>(url)
	};

	public getIntervenantsForAcces(accesId: number): Observable<IntervenantAccesDTO[]> {
		let url = this.path + "acces/" + accesId + "/intervenants?withRole=true";
		let result = this.httpClient.get<IntervenantAccesDTO[]>(url);
		return result;
	}

	public getEntreprisesForAcces(accesId: number): Observable<EntrepriseAccesDTO[]> {
		let url = this.path + "acces/" + accesId + "/entreprises?withRole=true";
		let result = this.httpClient.get<EntrepriseAccesDTO[]>(url);
		return result;
	}

	// Methode permettant de recuperer les ouvrages lies a un acces
	public getOuvrageDTOByAcces(idAcces: number): Observable<OuvrageAccesDTO[]> {
		let url = this.path + 'acces/' + idAcces + '/ouvrages?format=withEtatAcces';
		return this.httpClient //
			.get<OuvrageAccesDTO[]>(url) //
			;
	}

	// Methode permettant de recuperer les ouvrages lies a un acces
	public getOuvrageConcernesByAcces(idAcces: number): Observable<OuvrageAccesDTO[]> {
		let url = this.path + 'acces/' + idAcces + '/ouvrages-concernes';
		return this.httpClient.get<OuvrageAccesDTO[]>(url);
	}

	// Methode permettant de recuperer les ouvrages lies a un acces
	public getPendingOperationsForAcces(idAcces: number): Observable<AccesOperation[]> {
		let url = this.path + 'acces/pendingOperations/' + idAcces;
		return this.httpClient.get<AccesOperation[]>(url);
	}
	
	// Methode permettant de recuperer les operations liees a un acces
	public getOperationsTLRByAcces(accesId: number): Observable<AccesOperationTLR[]> {
		let url = this.path + "acces/" + accesId + "/operations";
		let result = this.httpClient //
			.get<AccesOperationTLR[]>(url) //
		;
		return result;
	}
	
	// Methode permettant de recuperer les DIs liees a un acces
	public getDemandeInterventionsByAcces(accesId: number): Observable<DemandeIntervention[]> {
		let url = this.path + "acces/" + accesId + "/demande-interventions";
		let result = this.httpClient //
			.get<DemandeIntervention[]>(url) //
		;
		return result;
	}

	public updateForFermeture(command: FermetureAccesOrder): Observable<number> {
		if ((window as any).forceMailsAsTest) {
			command.forceMailsAsTest = true;
		}
		
		let url = this.path + "acces/fermeture-orders";
		let result$ = this.httpClient.post<number>(url, command, {});
		return result$;
	}

	public updateForError(command: ErrorAccesOrder): Observable<number> {
		if ((window as any).forceMailsAsTest) {
			command.forceMailsAsTest = true;
		}
		
		let url = this.path + "acces/error-orders";
		let result$ = this.httpClient.post<number>(url, command, {});
		return result$;
	}

	// Fonction utilitaire pour obtenir les httpParams issu d'un objet AccesSearchCriteria
	private getCriteriaAsParams(searchCriteria: AccesSearchCriteria): HttpParams {
		let httpParams = new HttpParams();
		for (let key in searchCriteria) {
			let value = (searchCriteria as any)[key];
			if (value !== null && value !== undefined) {
				if (key === 'dateMin' || key === 'dateMax') {
					value = this.jsr310Service.toLocalDateParam2(value);
				}
				httpParams = httpParams.set(key, value);				
			}
		}
		return httpParams;
	}

	// Methode permettant de rechercher les acces
	public search(searchCriteria: AccesSearchCriteria): Observable<AccesSearchResult[]> {
		let url = this.path + 'acces/search';
		let httpParams = this.getCriteriaAsParams(searchCriteria);
		return this.httpClient.get<AccesSearchResult[]>(url, { params: httpParams });
	};

	// Methode permettant d'exporter les acces
	public export(searchCriteria: AccesSearchCriteria): Observable<Blob> {
		let url = this.path + 'acces/export';
		let httpParams = this.getCriteriaAsParams(searchCriteria);
		return this.httpClient.get(url, { params: httpParams, responseType: 'blob' });
	};

	// Methode permettant d'exporter les acces selon le format specifique des tarifs negatifs
	public exportForTarifNegatif(searchCriteria: AccesSearchCriteria): Observable<Blob> {
		let url = this.path + 'acces/exportForTarifNegatif';
		let httpParams = this.getCriteriaAsParams(searchCriteria);
		return this.httpClient.get(url, { params: httpParams, responseType: 'blob' });
	};
	
	public updateAssociationArrets(command: AssociationArretsOrder): Observable<number> {
		let url = this.path + "acces/association-arrets-orders";
		let result$ = this.httpClient.post<number>(url, command, {});
		return result$;
	}
	
	public changeEtatAcces(idAcces: number, newEtat: AccesEtat, controleSecurite: number | null): Observable<any> {
		let now = this.jsr310Service.toLocalDateTime(new Date());
		let data = {
			'idAcces': idAcces,
			'newEtatCode': newEtat,
			'changeDate': now,
			'forceMailsAsTest': (window as any).forceMailsAsTest,
			'controleSecurite': controleSecurite
		};
		let url = this.path + 'acces/updateEtat';
		return this.httpClient.put(url, data);
	};

	public demanderValidation(idAcces: number): Observable<any> {
		let url = this.path + 'acces/demande-validation-orders';
		return this.httpClient.post(url, idAcces);
	};
	
	public refuserAcces(refusData: any): Observable<any> {
		if ((window as any).forceMailsAsTest) {
			refusData.forceMailsAsTest = true;
		}

		let url = this.path + 'acces/refuser';
		return this.httpClient.put(url, refusData);
	};
	
	public supprimerAcces(accesId: number): Observable<any> {
		let url = this.path + 'acces/' + accesId;
		return this.httpClient.delete(url);
	};
	
	// Methode d'obtention des suggestions de numero d'acces
	public getSuggestionsNumeroAcces(criteria: string, quantity: number): Observable<Suggestions<number, AccesTLR>> {
		let url = this.path + 'acces/suggestions?criteria=' + encodeURI(criteria) + '&limit=' + quantity;
		let parameters = new HttpParams();
		parameters = parameters.set('criteria', criteria);
		parameters = parameters.set('limit', quantity);
		let result = this.httpClient.get<Suggestions<number, AccesTLR>>(url, { params: parameters });
		return result;
	}
	
	// Methode d'obtention du prochain numero d'acces disponible pour une antenne
	public getNumeroAcces(idAntenne: number): Observable<string> {
		let now = new Date();
		let annee = now.getFullYear();
		let url = this.path + 'acces/numero/antenne/' + idAntenne + '/annee/' + annee;
		let result = this.httpClient.get<string>(url);
		return result;
	};
	
	// Methode de creation d'un acces plannifie
	public createAsPlanned(command: CreationPlannedAccesOrder): Observable<number> {
		let url = this.path + 'acces/create-planned-orders';
		let result = this.httpClient.post<number>(url, command);
		return result;
	}
	
	// Methode de creation d'un acces plannifie
	public createAsGranted(command: CreationGrantedAccesOrder): Observable<number> {
		if ((window as any).forceMailsAsTest) {
			command.forceMailsAsTest = true;
		}
		
		let url = this.path + 'acces/create-granted-orders';
		let result = this.httpClient.post<number>(url, command);
		return result;
	}
	
	// Methode de creation d'un acces plannifie
	public createAsDenied(command: CreationDeniedAccesOrder): Observable<number> {
		if ((window as any).forceMailsAsTest) {
			command.forceMailsAsTest = true;
		}
		
		let url = this.path + 'acces/create-denied-orders';
		let result = this.httpClient.post<number>(url, command);
		return result;
	}
	
	// Methode de mise a jour d'un acces plannifie
	public updatePlanned(command: UpdatePlannedAccesOrder): Observable<number> {
		let url = this.path + 'acces/update-planned-orders';
		let result = this.httpClient.post<number>(url, command);
		return result;
	}
	
	// Methode de mise a jour d'un acces en cours
	public updateGranted(command: UpdateGrantedAccesOrder): Observable<number> {
		let url = this.path + 'acces/update-granted-orders';
		let result = this.httpClient.post<number>(url, command);
		return result;
	}
	
	// Methode de mise a jour d'un acces en plannifie, incluant sa validation
	public updateAndGrant(command: UpdateAndGrantAccesOrder): Observable<number> {
		if ((window as any).forceMailsAsTest) {
			command.forceMailsAsTest = true;
		}
		
		let url = this.path + 'acces/update-and-grant-orders';
		let result = this.httpClient.post<number>(url, command);
		return result;
	}

	// Methode de mise a jour d'un acces en plannifie, incluant son refuse
	public updateAndDeny(command: UpdateAndDenyAccesOrder): Observable<number> {
		if ((window as any).forceMailsAsTest) {
			command.forceMailsAsTest = true;
		}
		
		let url = this.path + 'acces/update-and-deny-orders';
		let result = this.httpClient.post<number>(url, command);
		return result;
	}
	
	// Methode d'associer un acces à des opérations
	public associerOperations(accesOperationBinder: AccesOperationBinderEntity): Observable<any> {
		let url = this.path + 'acces/associerOperations';
		return this.httpClient.put(url, accesOperationBinder);
	};
	
	// Methode permettant de mettre a jour les informations sur le rappel des intervenants
	public updateRappelIntervenants(command: UpdateRappelIntervenantsOrder): Observable<any> {
		let url = this.path + 'acces/update-rappel-intervenants-orders';
		let result = this.httpClient.post<number>(url, command);
		return result;
	}
	
	///////////////////////////////////////////////////////////////
	// Lancement de batch de traitements de masse pour les acces //
	///////////////////////////////////////////////////////////////
		
	// Methode de creation d'acces en masse pour le tarif negatif
	public launchCreateTarifNegatifAccesJob(command: CreationTarifNegatifAccesOrder): Observable<number> {
		let url = this.path + 'acces/create-tarif-negatif-orders';
		let result = this.httpClient.post<number>(url, command);
		return result;
	}
	
	// Methode de creation d'acces en masse pour le tarif negatif
	public launchModifyTarifNegatifAccesJob(command: UpdateTarifNegatifAccesOrder): Observable<number> {
		let url = this.path + 'acces/update-tarif-negatif-orders';
		let result = this.httpClient.post<number>(url, command);
		return result;
	}
	
	// Methode de demande de validation d'acces en masse
	public launchAskValidationAccesJob(ids: number[]): Observable<number> {
		let url = this.path + 'acces/mass-demande-validation-orders';
		let result = this.httpClient.post<number>(url, ids);
		return result;
	}
	
	// Methode d'authorisation d'acces en masse
	public launchChangeEtatAccesJob(command: MassChangeEtatOrder): Observable<number> {
		if ((window as any).forceMailsAsTest) {
			command.forceMailsAsTest = true;
		}
		
		let url = this.path + 'acces/mass-change-etat-orders';
		let result = this.httpClient.post<number>(url, command);
		return result;
	}

	// Methode de refus d'acces en masse
	public launchDenyAccesJob(command: MassDenyAccesOrder): Observable<any> {
		if ((window as any).forceMailsAsTest) {
			command.forceMailsAsTest = true;
		}

		let url = this.path + 'acces/mass-deny-acces-orders';
		let result = this.httpClient.post<number>(url, command);
		return result;
	};
	
	// Methode de fermeture d'acces en masse
	public launchCloseAccesJob(command: MassCloseAccesOrder): Observable<number> {
		if ((window as any).forceMailsAsTest) {
			command.forceMailsAsTest = true;
		}

		let url = this.path + 'acces/mass-close-acces-orders';
		let result = this.httpClient.post<number>(url, command);
		return result;
	}

	// Methode de suppression d'acces en masse
	public launchDeleteAccesJob(ids: number[]): Observable<number> {
		let url = this.path + 'acces/mass-delete-acces-orders';
		let result = this.httpClient.post<number>(url, ids);
		return result;
	}

	// Methode permettant de recuperer les ouvrages lies a un acces
	public findAccesIncomplets(): Observable<AccesIncomplet[]> {
		let url = this.path + 'acces/accesIncomplets';
		return this.httpClient.get<AccesIncomplet[]>(url);
	}

	public searchWorkOrders(accesId: number): Observable<WorkOrder[]> {
		return this.httpClient.get<WorkOrder[]>(
			this.path + 'acces/searchWorkOrders/' + accesId
		);
	}

	public searchNotifications(accesId: number): Observable<Notification[]> {
		return this.httpClient.get<Notification[]>(
			this.path + 'acces/searchNotifications/' + accesId
		);
	}

	public getAssociatedWorkOrders(accesId: number): Observable<WorkOrder[]> {
		return this.httpClient.get<WorkOrder[]>(
			this.path + 'acces/' + accesId + '/workorders'
		);
	}

	public getAssociatedNotifications(accesId: number): Observable<Notification[]> {
		return this.httpClient.get<Notification[]>(
			this.path + 'acces/' + accesId + '/notifications'
		);
	}
	
	public getAccesCoactifsWithPilotageEco(accesPilotageEcoOrder: AccesPilotageEcoOrder): Observable<number[]> {
		let url = this.path + "acces/getAccesCoactifsWithPilotageEco";
		return this.httpClient.post<number[]>(url, accesPilotageEcoOrder, {});
	}
	
	public demanderFermeture(command: FermetureAccesOrder): Observable<number> {
		if ((window as any).forceMailsAsTest) {
			command.forceMailsAsTest = true;
		}

		let url = this.path + "acces/demande-fermeture-orders";
		let result$ = this.httpClient.post<number>(url, command, {});
		return result$;
	}
	
	public getDemandeFermeture(accesId: number): Observable<FermetureAccesOrder> {
		let url = this.path + 'acces/demande-fermeture/' + accesId;
		return this.httpClient.get<FermetureAccesOrder>(url);
	}
	
	public approveDemandeFermeture(validationOrder: DemandeFermetureValidationOrder): Observable<DemandeFermetureValidationOrder> {
		let url = this.path + 'acces/approve-demande-fermeture';
		return this.httpClient.put<DemandeFermetureValidationOrder>(url, validationOrder);
	}

	public denyDemandeFermeture(validationOrder: DemandeFermetureValidationOrder): Observable<DemandeFermetureValidationOrder> {
		let url = this.path + 'acces/deny-demande-fermeture';
		return this.httpClient.put<DemandeFermetureValidationOrder>(url, validationOrder);
	}
	
	public getCCNotifEvents(userId: number): Observable<any> {
		let url = this.path + 'acces/cc-notifs/' + userId;
		return this.httpClient.get<NotificationForCC[]>(url);
	}
	
	public markCcNotifAsViewed(notifId: number): Observable<NotificationForCC[]> {
		let url = this.path + 'acces/notifs/cc/viewed/' + notifId;
		return this.httpClient.put<NotificationForCC[]>(url, {});
	}
}